import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import axios from '../../api/axios'
import useTranslation from '../../hooks/useTranslation'
import { useSnapshot } from 'valtio'
import Loader from '../../components/generic/Loader'
import SpinnerButton from '../../components/generic/SpinnerButton'

const EMAIL_REGEX = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

const Background = styled.div`
    background-size: 'cover';
    background-position: 'center';
    height: 100vh;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("/backgroundV2.jpg");
        background-size: repeat;
        background-position: center;
        filter: ${({ theme }) =>
            theme.mode === 'dark'
                ? 'blur(2px) invert(0.8) brightness(1) hue-rotate(160deg)'
                : 'blur(2px) invert(0) brightness(0.6) hue-rotate(-40deg)'};
        z-index: -1;
    }
`

const Center = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
`

const EntrySection = styled.section`
    background: ${({ theme }) => theme.font.background};
    color: ${({ theme }) => theme.font.color};
    display: grid;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    padding: 1.5rem;
    border-radius: 1rem;
    max-width: 350px;
    gap: 1rem;
`

const EntryTitle = styled.h2``

const Subtitle = styled.p`
    margin: 0;
    font-size: var(--font-S);
    color: ${({ theme }) => theme.font?.muted || '#6b7280'};
`

const Label = styled.label`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: var(--font-S);
`

const Input = styled.input`
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.border?.color || '#d1d5db'};
    border-radius: 0.5rem;
    font-size: 1rem;
    background: transparent;
    color: inherit;
    box-sizing: border-box;
`

const SuccessMsg = styled.p`
    color: ${({ theme }) => theme.font?.muted || '#6b7280'};
    font-size: var(--font-S);
    margin: 0;
`

const SmallP = styled.p`
    font-size: var(--font-XS);
`

const SmallLink = styled(Link)`
    font-size: var(--font-XS);
`

const ForgotPassword = () => {
    const { t, tState, loadTrans } = useTranslation()
    const transSnap = useSnapshot(tState)

    const [email, setEmail] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [loading, setLoading] = useState(false)
    const [isReady, setIsReady] = useState(false)

    const validEmail = EMAIL_REGEX.test(email)

    useEffect(() => { loadTrans() }, [])

    useEffect(() => {
        if (transSnap.ready && !isReady) setIsReady(true)
    }, [transSnap.ready])

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validEmail) return
        setLoading(true)
        try {
            await axios.post('/portal/requestResetPassword', { email })
        } catch {
            // Silently ignore — always show success to avoid leaking user existence
        }
        setSubmitted(true)
        setLoading(false)
    }

    if (!isReady) return <Loader />

    return (
        <Background>
            <Center>
                <EntrySection>
                    <EntryTitle>{t('Entry.form.forgot.password.title')}</EntryTitle>

                    {submitted ? (
                        <>
                            <SuccessMsg>{t('Entry.form.forgot.password.success')}</SuccessMsg>
                            <SmallLink to="/login">{t('Entry.form.forgot.password.back')}</SmallLink>
                        </>
                    ) : (
                        <>
                            <Subtitle>{t('Entry.form.forgot.password.subtitle')}</Subtitle>
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <Label>
                                    {t('Entry.form.email')}
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        autoFocus
                                    />
                                </Label>
                                <SpinnerButton
                                    width='300px'
                                    $variant='primary'
                                    disabled={!validEmail || loading}
                                    name='submit'
                                    time='4000'
                                >
                                    {loading ? t('Entry.form.forgot.password.sending') : t('Entry.form.forgot.password.button')}
                                </SpinnerButton>
                            </form>
                            <SmallP><SmallLink to="/login">{t('Entry.form.forgot.password.back')}</SmallLink></SmallP>
                        </>
                    )}
                </EntrySection>
            </Center>
        </Background>
    )
}

export default ForgotPassword
